import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CONSTANT, LoaderService, OrderService } from '@bst-frontend/shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, startWith } from 'rxjs/operators';

export interface PickupPayload {
  pickupLocationId: number | string;
  deliveries: Array<{
    deliveryLocationId: number | string;
    items: Array<{
      productId: number | string;
      subItemId?: number | string | null;
      quantityBox: number;
      weightKg: number;
      _id?: string;
    }>;
  }>;
}

@Component({
  selector: 'bst-frontend-pickup-location-modal',
  templateUrl: './pickup-location-modal.component.html',
  styleUrls: ['./pickup-location-modal.component.css'],
})
export class PickupLocationModalComponent implements OnInit {
  @Input() orderId!: string; // for save API
  @Input() dataToEdit: any; // pass when editing; otherwise null

  form!: FormGroup;

  @Input() userAddresses: any[] = [];
  @Input() companyAddresses: any[] = [];
  @Input() productData: any = [];

  pickupLocationOptions: string[] = CONSTANT.pickupLocationOptions;
  deliveryLocationOptions: string[] = CONSTANT.deliveryLocationOptions;
  isViewMode = false;

  constructor(
    private fb: FormBuilder,
    private activeModal: NgbActiveModal,
    private orderService: OrderService,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      pickupLocationId: [null, Validators.required],
      deliveries: this.fb.array([]),
    });

    console.log('this.dataToEdit::', this.dataToEdit);

    if (this.dataToEdit) {
      this.setForm(this.convertToPickupPayload(this.dataToEdit));
    } else {
      // start with one delivery + one item row
      this.addDelivery();
    }
  }

  convertToPickupPayload(response: any): PickupPayload {
    return {
      pickupLocationId: response.pickup?.address?.addressId || null,
      deliveries:
        response.deliveries?.map((d: any) => ({
          deliveryLocationId: d.address?.addressId || null,
          items:
            d.items?.map((it: any) => ({
              productId: it.product?.productId || null,
              // product.subItems in response may be object { productId: ... } or null
              subItemId: it.product?.subItems?.productId ?? null,
              quantityBox: it.planned?.qty || 0,
              weightKg: it.planned?.weight || 0,
              _id: it._id ?? undefined,
            })) || [],
        })) || [],
    };
  }

  // ---------- builders ----------
  private buildItem(
    row?: Partial<PickupPayload['deliveries'][number]['items'][number]>
  ): FormGroup {
    const fg = this.fb.group({
      productId: [row?.productId ?? null, Validators.required],
      subItemId: [{ value: row?.subItemId ?? null, disabled: true }],
      quantityBox: [
        row?.quantityBox ?? null,
        [Validators.required, Validators.min(1)],
      ],
      weightKg: [
        row?.weightKg ?? null,
        [Validators.required, Validators.min(1)],
      ],
      _id: [row?._id ?? null],
    });

    // wire product -> subItem enable/validator
    this.wireItemReactivity(fg);
    return fg;
  }

  private wireItemReactivity(itemFG: FormGroup): void {
    const productCtrl = itemFG.get('productId')!;
    const subItemCtrl = itemFG.get('subItemId')!;

    productCtrl.valueChanges
      .pipe(startWith(productCtrl.value))
      .subscribe((productId: string | null) => {
        const subItems = this.getSubItems(productId); // returns [] if none

        if (subItems?.length) {
          // enable + require when product has subItems
          if (subItemCtrl.disabled) subItemCtrl.enable({ emitEvent: false });
          subItemCtrl.setValidators(Validators.required);
        } else {
          // clear + disable + no validator when no subItems
          subItemCtrl.reset(null, { emitEvent: false });
          subItemCtrl.clearValidators();
          if (subItemCtrl.enabled) subItemCtrl.disable({ emitEvent: false });
        }
        subItemCtrl.updateValueAndValidity({ emitEvent: false });
      });
  }

  private buildDelivery(
    del?: Partial<PickupPayload['deliveries'][number]>
  ): FormGroup {
    return this.fb.group({
      deliveryLocationId: [
        del?.deliveryLocationId ?? null,
        Validators.required,
      ],
      items: this.fb.array(
        (del?.items?.length ? del.items : [{}]).map((i) => this.buildItem(i))
      ),
    });
  }

  // ---------- getters ----------
  get deliveriesFA(): FormArray {
    return this.form.get('deliveries') as FormArray;
  }
  itemsFA(deliveryIndex: number): FormArray {
    return this.deliveriesFA.at(deliveryIndex).get('items') as FormArray;
  }

  // ---------- array ops ----------
  addDelivery(): void {
    this.deliveriesFA.push(this.buildDelivery());
  }
  removeDelivery(i: number): void {
    this.deliveriesFA.removeAt(i);
  }
  addItem(deliveryIndex: number): void {
    this.itemsFA(deliveryIndex).push(this.buildItem());
  }
  removeItem(deliveryIndex: number, itemIndex: number): void {
    const deliveryArray = this.deliveriesFA; // main deliveries FormArray
    const itemsArray = deliveryArray
      .at(deliveryIndex)
      .get('items') as FormArray;

    if (!itemsArray || itemIndex < 0 || itemIndex >= itemsArray.length) {
      console.warn('Invalid delete request:', { deliveryIndex, itemIndex });
      return;
    }

    itemsArray.removeAt(itemIndex);

    // optional: force update of form state
    itemsArray.markAsDirty();
    itemsArray.updateValueAndValidity();
  }

  ctrl(path: string) {
    return this.form.get(path);
  }

  deliveryCtrl(i: number, name: string) {
    return (this.deliveriesFA.at(i) as FormGroup).get(name);
  }

  trackByIndex(index: number, _item: unknown): number {
    return index;
  }

  // ---------- edit mode patch ----------
  private setForm(data: PickupPayload): void {
    this.form.patchValue({ pickupLocationId: data.pickupLocationId });
    this.deliveriesFA.clear();
    data.deliveries.forEach((d) =>
      this.deliveriesFA.push(this.buildDelivery(d))
    );
  }

  getSubItems(productId: string | null) {
    if (!productId) return [];
    const product = this.productData.find((p: any) => p._id === productId);
    return product?.subItems || [];
  }

  private compactAddress(a: any) {
    if (!a) return null;
    const {
      addressId,
      companyName,
      contactName,
      contactNumber,
      pincode,
      city,
      locality,
      buildingName,
      landmark,
      type,
    } = a;
    return {
      addressId,
      companyName,
      contactName,
      contactNumber,
      pincode,
      city,
      locality,
      buildingName,
      landmark,
      type,
    };
  }

  // ---------- payload builder (produces product.subItems object) ----------
  preparePayload() {
    const v = this.form.value as {
      pickupLocationId: string;
      deliveries: Array<{
        deliveryLocationId: string;
        items: Array<{
          productId: string;
          subItemId?: string | null;
          quantityBox: number;
          weightKg: number;
          _id?: string | null;
        }>;
      }>;
    };

    const pickupAddr = this.userAddresses.find(
      (a) => a.addressId === v.pickupLocationId
    );
    const pickup = {
      address: this.compactAddress(pickupAddr),
    };

    const deliveries = v.deliveries.map((d) => {
      const addr = this.userAddresses.find(
        (a) => a.addressId === d.deliveryLocationId
      );

      const items = d.items.map((it) => {
        const prod = this.productData.find((p: any) => p._id === it.productId);
        const sub = prod?.subItems?.find((s: any) => s._id === it.subItemId);

        return {
          product: {
            // core product info
            productId: prod?._id ?? it.productId,
            code: prod?.code ?? '',
            name: prod?.name ?? '',
            price: prod?.price ?? null,
            bstOfficePrice: prod?.bstOfficePrice ?? null,
            // include subItems object when a sub item selected, otherwise null
            subItems: sub
              ? {
                  productId: sub._id,
                  code: sub.code,
                  name: sub.name,
                  price: sub.price,
                  bstOfficePrice: sub.bstOfficePrice,
                }
              : null,
          },
          planned: {
            qty: Number(it.quantityBox) || 0,
            weight: Number(it.weightKg) || 0,
          },
          _id: it._id ?? undefined,
        };
      });

      return {
        address: this.compactAddress(addr),
        items,
      };
    });

    return { pickup, deliveries };
  }

  // ---------- save ----------
  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const payload = this.preparePayload();
    this.isViewMode = true;
    this.loader.show();

    const request$ = this.dataToEdit
      ? this.orderService.updateOrderItemV2(
          this.orderId,
          this.dataToEdit._id,
          payload
        )
      : this.orderService.createOrderItemV2(this.orderId, payload);

    request$
      .pipe(
        finalize(() => {
          this.isViewMode = false;
          this.loader.hide();
        })
      )
      .subscribe({
        next: (resp) => {
          this.activeModal.close({ status: 'saved', data: resp });
        },
        error: (err) => {
          console.error('Save/update failed', err);
        },
      });
  }

  cancel() {
    this.activeModal.dismiss('cancel');
  }
}
